import React from 'react';
import { AlertTriangle, CheckCircle, XCircle, Bell, BellRing } from 'lucide-react';
import Card from '../../../components/base/Card';
import Button from '../../../components/base/Button';
import type { AlertaPresupuesto } from '../../../shared/types/presupuestos';

interface AlertasPresupuestoProps {
  alertas: AlertaPresupuesto[];
  onMarcarLeida: (alertaId: string) => void;
  className?: string;
}

export const AlertasPresupuesto: React.FC<AlertasPresupuestoProps> = ({
  alertas,
  onMarcarLeida,
  className = ''
}) => {
  const alertasNoLeidas = alertas.filter(alerta => !alerta.leida);

  if (alertas.length === 0) {
    return (
      <Card className={`p-6 text-center ${className}`}>
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">
          ¡Todo bajo control!
        </h3>
        <p className="text-gray-600">
          No tienes alertas de presupuesto en este momento.
        </p>
      </Card>
    );
  }

  const getAlertIcon = (tipo: AlertaPresupuesto['tipo']) => {
    switch (tipo) {
      case 'advertencia':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'limite':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return <Bell className="w-5 h-5 text-blue-500" />;
    }
  };

  const getAlertStyles = (tipo: AlertaPresupuesto['tipo'], leida: boolean) => {
    const baseStyles = leida ? 'opacity-60' : '';
    
    switch (tipo) {
      case 'advertencia':
        return `border-l-4 border-yellow-500 bg-yellow-50 ${baseStyles}`;
      case 'limite':
        return `border-l-4 border-red-500 bg-red-50 ${baseStyles}`;
      default:
        return `border-l-4 border-blue-500 bg-blue-50 ${baseStyles}`;
    }
  };

  const formatearFecha = (fecha: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    }).format(fecha);
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Header con resumen */}
      {alertasNoLeidas.length > 0 && (
        <div className="flex items-center space-x-2 mb-4">
          <BellRing className="w-5 h-5 text-red-500" />
          <span className="text-sm font-medium text-gray-900">
            {alertasNoLeidas.length} alerta{alertasNoLeidas.length !== 1 ? 's' : ''} pendiente{alertasNoLeidas.length !== 1 ? 's' : ''}
          </span>
        </div>
      )}

      {/* Lista de alertas */}
      <div className="space-y-3">
        {alertas.map((alerta) => (
          <Card
            key={alerta.id}
            className={`p-4 ${getAlertStyles(alerta.tipo, alerta.leida)}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1">
                {getAlertIcon(alerta.tipo)}
                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-semibold text-gray-900 mb-1">
                    {alerta.titulo}
                  </h4>
                  <p className="text-sm text-gray-700 mb-2">
                    {alerta.mensaje}
                  </p>
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span>Categoría: {alerta.presupuestoId}</span>
                    <span>{formatearFecha(alerta.fechaCreacion)}</span>
                  </div>
                </div>
              </div>
              
              {!alerta.leida && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onMarcarLeida(alerta.id)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Marcar leída
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};